import { useRef, useState } from "react";
import { UploadCloud, X, ImagePlus, Loader2, AlertTriangle } from "lucide-react";
import { api } from "../lib/nailApi";
import { useI18n } from "../i18n/I18nContext";

/**
 * Reusable admin image uploader with drag-and-drop.
 *
 * Props:
 *  - value          Current image URL (string, may be empty).
 *  - onChange(url)  Called with the uploaded public URL (or "" when cleared).
 *  - subfolder      Optional folder inside the DO Spaces bucket
 *                   (e.g. "events", "speakers", "sponsors", "articles").
 *  - label          Optional label.
 *  - aspect         "portrait" | "landscape" | "square"   (preview shape)
 *  - testId         Optional data-testid prefix for QA.
 *  - required       Show * badge next to label.
 *  - hint           Optional helper text below the uploader.
 */
export default function ImageUploader({
  value,
  onChange,
  subfolder,
  label,
  aspect = "landscape",
  testId,
  required = false,
  hint,
}) {
  const { lang } = useI18n();
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const previewShape = aspect === "portrait"
    ? "aspect-[4/5]"
    : aspect === "square"
      ? "aspect-square"
      : "aspect-[16/9]";

  const t = (vi, en) => (lang === "en" ? en : vi);

  const validate = (file) => {
    if (!file) return t("Không có tệp", "No file");
    if (!file.type?.startsWith("image/")) return t("Chỉ chấp nhận hình ảnh", "Only images are allowed");
    if (file.size > 10 * 1024 * 1024) return t("Tệp quá lớn (tối đa 10MB)", "File too large (max 10MB)");
    return "";
  };

  const upload = async (file) => {
    setError("");
    const invalid = validate(file);
    if (invalid) { setError(invalid); return; }

    const form = new FormData();
    form.append("file", file);
    if (subfolder) form.append("subfolder", subfolder);

    setUploading(true);
    setProgress(0);
    try {
      const res = await api.post("/admin/assets/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const total = e.total || file.size || 1;
          setProgress(Math.min(99, Math.round(((e.loaded || 0) * 100) / total)));
        },
      });
      const url = res?.data?.url;
      if (!url) throw new Error("No URL returned");
      setProgress(100);
      onChange?.(url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t("Tải lên thất bại", "Upload failed");
      setError(String(msg));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-xs font-semibold text-espresso mb-1.5">
          {label}{required && <span className="text-rose-500 ml-1">*</span>}
        </label>
      )}

      <div
        className={`relative rounded-2xl border-2 border-dashed transition-colors ${
          dragOver ? "border-emerald-500 bg-emerald-50/60" : "border-espresso/25 bg-linen-50/60"
        }`}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
        onDrop={onDrop}
        data-testid={testId ? `${testId}-dropzone` : undefined}
      >
        {dragOver && (
          <div className="absolute inset-0 rounded-2xl bg-emerald-500/10 border-2 border-emerald-500 pointer-events-none flex items-center justify-center z-10">
            <div className="text-emerald-700 font-semibold text-sm">
              {t("Thả để tải lên", "Drop to upload")}
            </div>
          </div>
        )}

        <div className="p-3 flex items-start gap-3">
          {/* Preview */}
          <div className={`${previewShape} w-28 shrink-0 rounded-xl overflow-hidden bg-linen-100 border border-espresso/10 flex items-center justify-center`}>
            {value ? (
              <img src={value} alt="uploaded" className="w-full h-full object-cover" data-testid={testId ? `${testId}-preview` : undefined} />
            ) : (
              <ImagePlus className="w-6 h-6 text-espresso-muted" />
            )}
          </div>

          {/* Controls */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid={testId ? `${testId}-btn` : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                  uploading ? "bg-espresso/40 text-linen cursor-wait" : "bg-espresso text-linen hover:bg-espresso-hover"
                }`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t("Đang tải", "Uploading")} {progress}%
                  </>
                ) : (
                  <>
                    <UploadCloud className="w-3.5 h-3.5" />
                    {value ? t("Đổi ảnh", "Change image") : t("Chọn ảnh", "Choose image")}
                  </>
                )}
              </button>
              <span className="text-[11px] text-espresso-muted">
                {t("hoặc kéo-thả ảnh vào đây", "or drag & drop an image here")}
              </span>
              {value && !uploading && (
                <button
                  type="button"
                  onClick={() => { onChange?.(""); setError(""); }}
                  data-testid={testId ? `${testId}-clear` : undefined}
                  className="ml-auto inline-flex items-center gap-1 text-espresso-muted text-xs hover:text-espresso"
                >
                  <X className="w-3.5 h-3.5" /> {t("Xóa", "Remove")}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid={testId ? `${testId}-input` : undefined}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
              />
            </div>

            {uploading && (
              <div className="mt-2 h-1.5 w-full rounded-full bg-linen overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}

            {error && (
              <p className="mt-2 flex items-center gap-1.5 text-[11px] text-rose-600" data-testid={testId ? `${testId}-error` : undefined}>
                <AlertTriangle className="w-3.5 h-3.5" /> {error}
              </p>
            )}

            {hint && !error && (
              <p className="mt-2 text-[11px] text-espresso-muted">{hint}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
